import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  useContentToken,
  usePropertyField,
  SectionContextProvider,
  EditableToken,
  RichTextContent,
} from '@vibesites/runtime/react';

// Google Maps embed key (optional — falls back to keyless embed when absent).
const MAPS_API_KEY =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.GOOGLE_MAPS_API_KEY) || '';

// Panel behind the lead form — uses the theme's primary brand color.
const PANEL_BG = 'var(--vibe-color-primary)';

// ============================================================================
// Component
// ============================================================================

export function LetsConnect() {
  const formSlot = useBlockSlot('letsconnect.form_block', {
    defaultValue: 'multihub-lead-form',
  });

  const mapHeading = useContentToken('letsconnect.map_heading');

  const lat = usePropertyField('property_address_latitude') as string | null;
  const lng = usePropertyField('property_address_longitude') as string | null;
  const address = usePropertyField('property_full_address') as string | null;

  const hasCoords = !!(lat && lng && lat.trim() !== '' && lng.trim() !== '');

  let mapSrc = '';
  if (hasCoords && MAPS_API_KEY) {
    mapSrc = `https://www.google.com/maps/embed/v1/place?key=${MAPS_API_KEY}&q=${lat},${lng}&zoom=15`;
  } else if (hasCoords) {
    mapSrc = `https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`;
  } else if (address && address.trim() !== '') {
    mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(address.trim())}&output=embed`;
  }

  return (
    <section className="lc-section" aria-label={mapHeading as string | undefined}>
      <div className="lc-grid">
        {/* ── Left: dark panel with copy + lead form ── */}
        <div className="lc-left" style={{ backgroundColor: PANEL_BG }}>
          <div className="lc-inner">
            <RichTextContent
              path="letsconnect.heading"
              label="Heading"
              defaultValue="<p>Let's Connect</p>"
              as="h2"
              className="lc-heading"
            />

            <RichTextContent
              path="letsconnect.body"
              label="Intro Paragraph"
              defaultValue={
                '<p>Have questions about how to start living at your new home? Our dedicated leasing team at <span data-type="field-token" data-field="property_name"></span> is here to assist you.</p>'
              }
              as="div"
              className="lc-body"
            />

            <div className="lc-form-panel">
              <EditableToken path="letsconnect.form_block" as="div">
                {formSlot ? (
                  <SectionContextProvider sectionId={formSlot.sectionId}>
                    <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                  </SectionContextProvider>
                ) : (
                  <p className="lc-form-empty">No form block selected</p>
                )}
              </EditableToken>
            </div>
          </div>
        </div>

        {/* ── Right: location map ── */}
        <div className="lc-right">
          <EditableToken path="letsconnect.map_heading" as="h2" className="lc-sr-only">
            {mapHeading}
          </EditableToken>

          {mapSrc ? (
            <iframe
              className="lc-map"
              src={mapSrc}
              title={(mapHeading as string | undefined) || 'Property location map'}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          ) : (
            <div className="lc-map-placeholder">
              <p>Map location not available</p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .lc-section { width: 100%; background: ${PANEL_BG}; }

        .lc-grid {
          display: grid;
          grid-template-columns: 1fr;
          align-items: stretch;
        }
        @media (min-width: 1024px) {
          .lc-grid { grid-template-columns: 1fr 1fr; }
        }

        /* ── Left panel ── */
        .lc-left {
          display: flex;
          justify-content: center;
          padding: 3rem 1.5rem;
        }
        @media (min-width: 768px) {
          .lc-left { padding: 4.5rem 3rem; }
        }
        .lc-inner {
          width: 100%;
          max-width: 640px;
        }

        .lc-heading {
          margin: 0;
          font-family: 'Space Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
          font-weight: 400;
          font-size: 2.5rem;
          line-height: 1.1;
          color: #ffffff;
          text-align: center;
        }
        @media (min-width: 768px) {
          .lc-heading { font-size: 3rem; }
        }

        .lc-body {
          margin: 1.25rem auto 0;
          max-width: 34rem;
          font-family: 'Space Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 1rem;
          line-height: 1.7;
          color: rgba(255, 255, 255, 0.85);
          text-align: center;
        }

        .lc-form-panel { margin-top: 2.5rem; }
        .lc-form-empty {
          font-family: 'Space Mono', ui-monospace, monospace;
          color: rgba(255, 255, 255, 0.7);
          text-align: center;
        }

        /* ── Restyle the embedded lead-form block to the dark / underline look.
              The block uses inline styles, so these overrides use !important. ── */
        .lc-form-panel {
          --vibe-color-background: transparent;
          --vibe-radius-md: 0px;
          --vibe-radius-lg: 0px;
        }
        .lc-form-panel .mh-lead-form-card {
          background: transparent !important;
          box-shadow: none !important;
          padding: 0 !important;
        }
        /* Hide the block's own centered header/subheader — we render our own. */
        .lc-form-panel .mh-lead-form-card > h3,
        .lc-form-panel .mh-lead-form-card > p {
          display: none !important;
        }
        .lc-form-panel input,
        .lc-form-panel select,
        .lc-form-panel textarea {
          border: 0 !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.35) !important;
          border-radius: 0 !important;
          background: transparent !important;
          color: #ffffff !important;
          font-family: 'Space Mono', ui-monospace, monospace !important;
          padding-left: 0 !important;
          padding-right: 1.75rem !important;
        }
        .lc-form-panel textarea { padding-right: 0 !important; }
        .lc-form-panel input:focus,
        .lc-form-panel select:focus,
        .lc-form-panel textarea:focus {
          border-bottom-color: var(--vibe-color-primary) !important;
        }
        .lc-form-panel ::placeholder { color: rgba(255, 255, 255, 0.6) !important; }
        .lc-form-panel select option { color: #111827; }
        /* Match the ConvenienceCallout CTA: secondary fill, Oswald, uppercase,
           sharp corners, invert to white on hover. */
        /* Center the button within its full-width EditableSlotGroup wrapper.
           The button is inline-flex, so text-align on the wrapper centers it. */
        .lc-form-panel form > div:has(> .mh-lead-form-submit-button) {
          text-align: center;
        }
        .lc-form-panel .mh-lead-form-submit-button {
          /* Content-width like the CTA (inline-flex + px-10 py-4), centered. */
          width: auto !important;
          max-width: 100% !important;
          padding: 1rem 2.5rem !important;
          background-color: var(--vibe-color-secondary) !important;
          color: var(--vibe-color-text-on-secondary) !important;
          font-family: 'Oswald', 'Arial Narrow', system-ui, sans-serif !important;
          font-weight: 400 !important;
          font-size: 0.875rem !important;
          border-radius: 0 !important;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1) !important;
          transition: background-color 0.2s, color 0.2s !important;
          margin-top: 1rem;
        }
        .lc-form-panel .mh-lead-form-submit-button:hover:not(:disabled) {
          background-color: #ffffff !important;
          color: var(--vibe-color-secondary) !important;
        }

        /* ── Right map ── */
        .lc-right { position: relative; min-height: 360px; }
        .lc-map, .lc-map-placeholder {
          display: block;
          width: 100%;
          height: 100%;
          min-height: 360px;
          border: none;
        }
        @media (min-width: 1024px) {
          .lc-right { min-height: 100%; }
          .lc-map, .lc-map-placeholder { position: absolute; inset: 0; height: 100%; }
        }
        .lc-map-placeholder {
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--vibe-color-surface, #f5f5f5);
          font-family: var(--vibe-font-body);
          color: var(--vibe-color-text-muted);
        }

        .lc-sr-only {
          position: absolute;
          width: 1px; height: 1px;
          padding: 0; margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'LetsConnect',
  displayName: "Let's Connect (Form + Map)",
  tokens: {
    'letsconnect.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: "<p>Let's Connect</p>",
    },
    'letsconnect.body': {
      label: 'Intro Paragraph',
      type: 'richtext',
      defaultValue:
        '<p>Have questions about how to start living at your new home? Our dedicated leasing team at <span data-type="field-token" data-field="property_name"></span> is here to assist you.</p>',
    },
    'letsconnect.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
    'letsconnect.map_heading': {
      label: 'Map Heading (screen-reader label)',
      type: 'text',
      defaultValue: 'Find Us',
    },
  },
};

(LetsConnect as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default LetsConnect;
